import { useState, useEffect, useMemo } from "react";

import {
  AlertOctagon,
  ArrowRight,
  ArrowUpRightFromSquare,
  Crown
} from "lucide-react";
import { connect, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { Badge, Button, Progress, Spinner } from "reactstrap";
import { bindActionCreators } from "redux";

import { logout } from "../../../../actions/baseActions";
import ProtectedRoute from "../../../../components/ProtectedRoute";
import URL from "../../../../config/api";
import axios from "../../../../config/axios";
import { friendlyDate } from "../../../../utils/date";
import OlympicsLogo from "../../Conecta.png";

function StudentOlympicsLeaderboard({ ...props }) {
  const olympicsId = props.match.params.id;

  const [leaderboardData, setLeaderboardData] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const userId = props.user.user.id;

  const currUser = useMemo(
    () => leaderboardData?.ranking.find(student => userId === student.id),
    [leaderboardData]
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!leaderboardData) return;
      const newLeaderboard = {
        ...leaderboardData,
        end: getDateData(leaderboardData?.end?.date)
      };

      if (!newLeaderboard.color) newLeaderboard.color = "primary";

      setLeaderboardData(newLeaderboard);
    }, 1000);

    fetchLeaderboardData();

    return () => clearInterval(intervalId);
  }, []);

  const olympicsStatus = useMemo(() => {
    const currentDate = new Date();

    return currentDate >= new Date(leaderboardData?.olympic?.end)
      ? "Finalizada"
      : "Em progresso";
  }, [leaderboardData]);

  const fetchLeaderboardData = async () => {
    try {
      const leaderboardResponse = await axios.get(
        `${URL.api}olympics/rankingBySerie/${olympicsId}`,
        {
          headers: { Authorization: `Bearer ${props.user.token}` }
        }
      );

      const { data } = leaderboardResponse.data;
      setLeaderboardData(data);
    } catch (error) {
      toastr.error("Erro!", error.response.data.error);
      setErrorMessage(error.response.data.error);
    }
  };

  const getDateData = date => {
    const newDate = new Date(date);
    newDate.setHours(newDate.getHours() + 3);

    const simpleDate = [
      newDate.getFullYear(),
      newDate.getMonth() + 1,
      newDate.getDate()
    ]
      .reverse()
      .join("/");

    const daysLeft = Math.floor(
      (newDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    const hoursLeft = Math.floor(
      (newDate.getTime() - new Date().getTime()) / (1000 * 60 * 60)
    );
    const minutesLeft = Math.floor(
      (newDate.getTime() - new Date().getTime()) / (1000 * 60)
    );

    return {
      date: newDate,
      simpleDate,
      daysLeft,
      hoursLeft: hoursLeft % 24,
      minutesLeft: minutesLeft % 60
    };
  };

  const handleSplicedDate = date => {
    const newDate = friendlyDate(date);
    return newDate.substring(0, newDate.indexOf(", às"));
  };

  return (
    <div className="container">
      {leaderboardData ? (
        <>
          <section className="p-3 rounded-lg border d-flex flex-column mb-3">
            <div className="d-flex flex-column flex-md-row align-items-center h-100">
              <div
                className={`olympics-dashboard-logo d-flex align-items-center justify-content-center rounded p-2 w-100 mb-3 mb-md-0 mr-xl-3 bg-${leaderboardData.olympic.color}`}
              >
                <img
                  src={OlympicsLogo}
                  alt=""
                  style={{ objectFit: "cover", height: "180px" }}
                />
              </div>
              <div className="d-flex flex-column justify-content-between h-100 w-100">
                <div className="d-flex flex-column mb-4">
                  <div className="d-flex align-items-center mb-2">
                    <h3 className="mb-0 mr-2 font-weight-bold">
                      {leaderboardData.olympic.name}
                    </h3>
                    <Badge
                      color={leaderboardData.olympic.color}
                      className="text-white"
                    >
                      {olympicsStatus}
                    </Badge>
                  </div>

                  <small className="text-muted w-100">
                    {leaderboardData.olympic.description}
                  </small>

                  <div className="mt-4">
                    <h5 className="mb-0">Parabéns, {currUser?.name}!</h5>
                    <p className="mb-0">
                      Você ficou em{" "}
                      <strong className="text-orange">
                        {leaderboardData.ranking.findIndex(
                          student => userId === student.id
                        ) + 1}
                        º{" "}
                      </strong>
                      lugar com{" "}
                      <strong className="text-orange">
                        {currUser?.score}{" "}
                      </strong>
                      pontos.
                    </p>
                    {currUser?.score >= leaderboardData?.ranking[2].score && (
                      <p className="mb-0">
                        Procure o tutor da sua escola para informações da sua
                        premiação!
                      </p>
                    )}
                  </div>
                </div>

                <Button
                  href="/school/leaderboard/1"
                  className="d-flex align-items-center text-white align-self-start"
                  color="orange"
                  size="sm"
                >
                  Veja o placar das escolas aqui!{" "}
                  <ArrowUpRightFromSquare size={14} className="ml-2" />
                </Button>
              </div>

              <div
                className="flex-column d-flex align-items-center justify-content-center pt-5 w-100 h-100"
                style={{ maxWidth: "208px", aspectRatio: "1/1" }}
              >
                <div className="d-flex align-items-center mb-2">
                  <Crown size={24} className="text-yellow mr-2" />
                  <h5 className="m-0 font-weight-bold">1º lugar</h5>
                </div>
                <p className="mb-2 text-center">
                  {leaderboardData.ranking[0].name}
                </p>
                <Badge color="yellow" className="text-white mb-2">
                  {leaderboardData.ranking[0].score}pts
                </Badge>
                <div className="flex-1 w-100 bg-yellow-translucent rounded-top"></div>
              </div>
            </div>
          </section>

          <section className=" p-3 border w-100 rounded-lg d-flex flex-column">
            <h4>
              <strong>
                Placar de estudantes - {leaderboardData.serie.name}
              </strong>
            </h4>
            <div
              className="h-100 overflow-auto"
              style={{ maxHeight: "720px", minHeight: "300px" }}
            >
              {!leaderboardData.ranking ? (
                <div className="d-flex align-items-center justify-content-center w-100 h-100 rounded bg-light text-muted">
                  <Spinner color="secondary" size="sm" className="mr-1" />
                  Carregando escolas...
                </div>
              ) : (
                <ul className="d-flex flex-column list-unstyled p-0 m-0 h-100">
                  {leaderboardData.ranking.map((student, index) => (
                    <div
                      key={student.id}
                      className={`w-100 h-100 p-2 mb-2 rounded d-flex align-items-center text-decoration-none bg-${
                        index === 0
                          ? "yellow"
                          : index === 1
                          ? "slate"
                          : index === 2
                          ? "orange"
                          : userId === student.id
                          ? "purple"
                          : "secondary"
                      }-translucent`}
                      style={{ maxHeight: "72px" }}
                    >
                      <div
                        style={{ aspectRatio: "1/1", minWidth: "50px" }}
                        className={`p-2 h-100 mr-3 d-flex align-items-center rounded-sm justify-content-center bg-${
                          index === 0
                            ? "yellow"
                            : index === 1
                            ? "secondary"
                            : index === 2
                            ? "orange"
                            : userId === student.id
                            ? "purple"
                            : "transparent"
                        }`}
                      >
                        <h4
                          className={`m-0 text-${
                            index < 3 || userId === student.id
                              ? "white"
                              : "dark"
                          }`}
                        >
                          <strong>{index + 1}</strong>
                        </h4>
                      </div>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                          <h6
                            className="font-weight-normal m-0 text-nowrap text-dark"
                            title={student.name}
                          >
                            {student.name}
                          </h6>
                          <small className="text-muted">
                            {student.school_name}
                          </small>
                        </div>

                        <h5
                          className="text-muted m-0 text-right"
                          title={student.discipline_name}
                          style={{ minWidth: "0" }}
                        >
                          <strong className="mr-1 text-dark">
                            {student.score}
                          </strong>
                          pontos
                        </h5>
                      </div>
                    </div>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </>
      ) : errorMessage ? (
        <div className="d-flex align-items-center justify-content-center w-100 h-100 pt-5">
          <AlertOctagon size={56} className="text-danger mr-2" />
          <div className="d-flex flex-column">
            <h3 className="mb-0 font-weight-bold text-danger">Atenção!</h3>
            <p className="mb-0 text-muted">{errorMessage}</p>
          </div>
        </div>
      ) : (
        <div className="d-flex align-items-center justify-content-center w-100 h-100 pt-5">
          <Spinner color="secondary" size="sm" className="mr-2" />
          Carregando dados da olimpíada...
        </div>
      )}
      <ProtectedRoute routeId={props.currentRoute} />
    </div>
  );
}

const mapStateToProps = state => ({
  user: state.user,
  general: state.general,
  profile: state.profile,
  form: state.form
});

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      logout
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StudentOlympicsLeaderboard);
